import { FC, useMemo, useEffect, useCallback, useState } from 'react'
import styles from 'components/features/future/FutureAssumptionsPane/FutureAssumptionsPane.styl'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { FloatingPaneHeader } from 'components/features/pfm/FloatingPaneHeader/FloatingPaneHeader'
import { FloatingPaneFooter } from 'components/features/pfm/FloatingPaneFooter/FloatingPaneFooter'
import { FutureInputDecoration } from 'components/features/future/FutureInputDecoration/FutureInputDecoration'
import { FutureMarriedInput } from 'components/features/future/FutureMarriedInput/FutureMarriedInput'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { getAssumptionsActionCreator, patchAssumptionsActionCreator, resetAssumptionsActionCreator } from 'components/features/future/actions'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import { FutureAssumptions, FutureAssumptionsHints, FutureAssumptionsTaxHints } from 'components/features/future/types'
import * as yup from 'yup'
import { ObjectSchema } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAssumptionsTaxHints } from 'components/features/future/helpers'
import { LoadingOverlay } from 'components/atoms/preloaders/LoadingOverlay'
import { FutureFieldButtons } from 'components/features/future/FutureField/FutureFieldButtons'
import { FutureFieldSlider } from 'components/features/future/FutureField/FutureFieldSlider'
import { maskPercentFromZero, maskCurrencyFromZero, maskPercentNeg30To50, maskPercentNeg10To20 } from 'components/atoms/inputs/maskedInput'
import { schemaNumber, schemaNumberInString } from 'schemas/baseSchema'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { Omit } from 'interfaces'
import { FutureAssumptionsOverlay } from 'components/features/future/FutureAssumptionsPane/FutureAssumptionsOverlay'
import { ThemeProvider } from 'components/features/theme/ThemeProvider/ThemeProvider'
import { AccordionTabFocusinPreset } from 'components/atoms/accordion/AccordionTabFocusinPreset/AccordionTabFocusinPreset'
import { schemaMaritalStatus } from 'schemas/futureSchema'
import { useCollectErrors } from 'components/features/future/hooks/useCollectErrors'

const schema = yup.object({
  marital_status: schemaMaritalStatus,
  annual_salary: schemaNumber,
  asset_auto: schemaNumber,
  asset_auto_rate: schemaNumberInString,
  asset_cd: schemaNumber,
  asset_cd_rate: schemaNumberInString,
  asset_home: schemaNumber,
  asset_home_rate: schemaNumberInString,
  asset_investment: schemaNumber,
  asset_investment_rate: schemaNumberInString,
  asset_other: schemaNumber,
  asset_other_rate: schemaNumberInString,
  asset_savings: schemaNumber,
  asset_savings_rate: schemaNumberInString,
  debt_auto: schemaNumber,
  debt_creditcard: schemaNumber,
  debt_creditcard_rate: schemaNumberInString,
  debt_home_equity: schemaNumber,
  debt_mortgage: schemaNumber,
  debt_other: schemaNumber,
  debt_student: schemaNumber,
  inflation_rate: schemaNumberInString,
  monthly_spending_housing: schemaNumber,
  monthly_spending_loans: schemaNumber,
  monthly_spending_other: schemaNumber,
  post_retirement_expense_reduction_rate: yup.number(),
  salary_growth_rate: schemaNumberInString,
  salary_tax_rate: schemaNumberInString,
}) as ObjectSchema<Omit<FutureAssumptions, 'hints'>>

type FutureAssumptionsPaneProps = {
  onCancel: () => void
}

export const FutureAssumptionsPane: FC<FutureAssumptionsPaneProps> = (props) => {
  const { onCancel } = props

  const dispatch = useStoreDispatch()
  useEffect(() => {
    dispatch(getAssumptionsActionCreator.request({}))
  }, [dispatch])

  const { isLoading, hasData } = useStoreSelector((state) => {
    const { assumptions } = state.data.future
    return {
      isLoading: assumptions.isLoading,
      hasData: !!assumptions.data,
    }
  })

  return (
    <>
      { hasData && !isLoading && (
        <FormContext onCancel={ onCancel } />
      )}
      { isLoading && (
        <div className={ styles.loading }>
          <LoadingOverlay />
        </div>
      )}
    </>
  )
}

const FormContext: FC<FutureAssumptionsPaneProps> = (props) => {
  const { onCancel } = props

  const { data } = useStoreSelector((state) => state.data.future.assumptions)
  const { hints, ...restValues } = {
    ...data,
  } as FutureAssumptions

  const formContext = useForm<FutureAssumptions>({
    defaultValues: {
      ...restValues,
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  })
  const { handleSubmit } = formContext

  const headerChild = useMemo(() => (
    <FloatingPaneHeader
      title='Forecast Assumptions'
      iconSlug='chart_gears'
      centered
    />
  ), [])

  const dispatch = useStoreDispatch()

  const handleSuccessSubmit = useMemo(() => handleSubmit((formData) => {
    dispatch(patchAssumptionsActionCreator.request(formData))
  }), [handleSubmit, dispatch])

  const handleResetAssumptions = useCallback(() => {
    dispatch(resetAssumptionsActionCreator.request({}))
  }, [dispatch])

  const isLoading = useStoreSelector((state) => state.data.future.request.isLoading)

  const footerChild = useMemo(() => (
    <FloatingPaneFooter
      actionName='Update Assumptions'
      isLoading={ isLoading }
      onAction={ handleSuccessSubmit }
      onCancel={ onCancel }
    />
  ), [handleSuccessSubmit, onCancel, isLoading])

  const notificationChild = useMemo(() => (
    <FutureAssumptionsOverlay />
  ), [])

  const resetButtonChild = useMemo(() => (
    <button type='button' className={ styles.resetButton } onClick={ handleResetAssumptions }>
      Reset to default values
    </button>
  ), [handleResetAssumptions])

  return (
    <FormProvider { ...formContext }>
      <form onSubmit={ handleSuccessSubmit }>
        <FloatingPaneLayout
          type='future'
          header={ headerChild }
          footer={ footerChild }
          notification={ notificationChild }
          onClose={ onCancel }
          withStaticHeader
          bodyClassName={ styles.futureAssumptionsPane }
        >
          <Form />
          { resetButtonChild }
        </FloatingPaneLayout>
      </form>
    </FormProvider>
  )
}

const DEFAULT_RATES = {
  asset_savings_rate: '0.1',
  asset_cd_rate: '2.8',
  asset_investment_rate: '5.0',
  asset_other_rate: '0.3',
  asset_home_rate: '1.8',
  asset_auto_rate: '20',
  debt_creditcard_rate: '13.75',
}

const filterDefaults = (hints: FutureAssumptionsHints, hintKey: keyof typeof DEFAULT_RATES, note: string) => {
  if (parseFloat(hints[hintKey]) === parseFloat(DEFAULT_RATES[hintKey])) {
    return null
  }
  return note
}

const Form: FC = () => {
  const errors = useCollectErrors<FutureAssumptions>()
  const { data } = useStoreSelector((state) => state.data.future.assumptions)
  const { watch, register } = useFormContext<FutureAssumptions>()
  const { hints } = { ...data } as FutureAssumptions

  const [taxHints, setTaxHints] = useState<Partial<FutureAssumptionsTaxHints>>({})
  useAssumptionsTaxHints(setTaxHints)

  const [
    monthly_spending_housing,
    monthly_spending_loans,
    monthly_spending_other,
  ] = watch(['monthly_spending_housing', 'monthly_spending_loans', 'monthly_spending_other'])

  const totalChild = useMemo(() => {
    const totalSpending = (+monthly_spending_housing || 0) + (+monthly_spending_loans || 0) + (+monthly_spending_other || 0)
    return (
      <div>
        <div className={ styles.label }>Total Spending</div>
        <div className={ styles.note }>Your peers are spending around { formatUSCurrency(hints.monthly_spending_other_peers) }.</div>
        <div className={ styles.total }>
          { formatUSCurrency(totalSpending) }
          <div className={ styles.units }>per month</div>
        </div>
      </div>
    )
  }, [monthly_spending_housing, monthly_spending_loans, monthly_spending_other, hints.monthly_spending_other_peers])

  return (
    <div className={ styles.fieldsWrapper }>
      <FutureInputDecoration label='Marital Status'>
        <FutureMarriedInput name='marital_status' register={ register } />
      </FutureInputDecoration>
      <div className={ styles.tab }>
        <ThemeProvider futureFieldTitle='income'>
          <AccordionTabFocusinPreset label='Current Income' initialOpen>
            <div className={ styles.salaryField }>
              <FutureFieldSlider
                name='annual_salary'
                mask={ maskCurrencyFromZero }

                note='How much you make before taxes.'
                error={ errors.annual_salary }
                units='per year'

                min={ 0 }
                max={ 500000 }
                step={ 2500 }
              />
            </div>
          </AccordionTabFocusinPreset>
        </ThemeProvider>
      </div>

      <div className={ styles.tab }>
        <ThemeProvider futureFieldTitle='member'>
          <AccordionTabFocusinPreset label='Current Spending' initialOpen>
            <FutureFieldSlider
              label='Housing Spending'
              name='monthly_spending_housing'
              mask={ maskCurrencyFromZero }

              error={ errors.monthly_spending_housing }
              note={ 'Include your rent, mortgage, property taxes, and homeowner\'s insurance.' }
              units='per month'

              min={ 0 }
              max={ 10000 }
              step={ 50 }
            />

            <FutureFieldSlider
              label='Loan Payments'
              name='monthly_spending_loans'
              mask={ maskCurrencyFromZero }

              error={ errors.monthly_spending_loans }
              note='Do not include mortgages and credit cards.'
              units='per month'

              min={ 0 }
              max={ 10000 }
              step={ 50 }
            />

            <FutureFieldSlider
              label='Other Spending'
              name='monthly_spending_other'
              mask={ maskCurrencyFromZero }

              error={ errors.monthly_spending_other }
              note='This includes all your other spending.'
              units='per month'

              min={ 0 }
              max={ 12000 }
              step={ 50 }
            />

            { totalChild }

          </AccordionTabFocusinPreset>
        </ThemeProvider>

      </div>

      <div className={ styles.tab }>
        <ThemeProvider futureFieldTitle='green'>
          <AccordionTabFocusinPreset label='Current Assets'>
            <FutureFieldSlider
              label='Cash &amp; Savings Balance'
              name='asset_savings'
              mask={ maskCurrencyFromZero }

              error={ errors.asset_savings }
              note={ `Your linked Cash & Savings accounts have a balance of ${ formatUSCurrency(hints.asset_savings) }.` }

              min={ 0 }
              max={ 150000 }
              step={ 100 }
            />

            <FutureFieldSlider
              label='CDs Balance'
              name='asset_cd'
              mask={ maskCurrencyFromZero }

              error={ errors.asset_cd }
              note={ `Your linked CD accounts have a balance of ${ formatUSCurrency(hints.asset_cd) }.` }

              min={ 0 }
              max={ 500000 }
              step={ 5000 }
            />

            <FutureFieldSlider
              label='Investments Balance'
              name='asset_investment'
              mask={ maskCurrencyFromZero }

              error={ errors.asset_investment }
              note={ `Your linked Investment accounts have a balance of ${ formatUSCurrency(hints.asset_investment) }.` }

              min={ 0 }
              max={ 1000000 }
              step={ 1000 }
            />

            <FutureFieldSlider
              label='Other Assets Balance'
              name='asset_other'
              mask={ maskCurrencyFromZero }

              error={ errors.asset_other }
              note={ `Your Other linked asset accounts have a balance of ${ formatUSCurrency(hints.asset_other) }.` }

              min={ 0 }
              max={ 500000 }
              step={ 500 }
            />

            <FutureFieldSlider
              label='Homes Value'
              name='asset_home'
              mask={ maskCurrencyFromZero }

              error={ errors.asset_home }
              note={ `Your linked Homes have a value of ${ formatUSCurrency(hints.asset_home) }.` }

              min={ 0 }
              max={ 2000000 }
              step={ 1000 }
            />

            <FutureFieldSlider
              label='Cars Value'
              name='asset_auto'
              mask={ maskCurrencyFromZero }

              error={ errors.asset_auto }
              note={ `Your linked Cars have a value of ${ formatUSCurrency(hints.asset_auto) }.` }

              min={ 0 }
              max={ 100000 }
              step={ 100 }
            />
          </AccordionTabFocusinPreset>
        </ThemeProvider>
      </div>

      <div className={ styles.tab }>
        <ThemeProvider futureFieldTitle='orange'>
          <AccordionTabFocusinPreset label='Current Debts'>
            <FutureFieldSlider
              label='Credit Cards Balance'
              name='debt_creditcard'
              mask={ maskCurrencyFromZero }

              error={ errors.debt_creditcard }
              note={ `Your current Credit Cards balance from your linked accounts is ${ formatUSCurrency(hints.debt_creditcard) }.` }

              min={ 0 }
              max={ 75000 }
              step={ 100 }
            />

            <FutureFieldSlider
              label='Personal &amp; Other Loans Balance'
              name='debt_other'
              mask={ maskCurrencyFromZero }

              error={ errors.debt_other }
              note={ `Your current Personal & Other Loans balance from your linked accounts is ${ formatUSCurrency(hints.debt_other) }.` }

              min={ 0 }
              max={ 100000 }
              step={ 100 }
            />

            <FutureFieldSlider
              label='Auto Loans Balance'
              name='debt_auto'
              mask={ maskCurrencyFromZero }

              error={ errors.debt_auto }
              note={ `Your current Auto Loans balance from your linked accounts is ${ formatUSCurrency(hints.debt_auto) }.` }

              min={ 0 }
              max={ 50000 }
              step={ 100 }
            />

            <FutureFieldSlider
              label='Mortgages Balance'
              name='debt_mortgage'
              mask={ maskCurrencyFromZero }

              error={ errors.debt_mortgage }
              note={ `Your current Mortgages balance from your linked accounts is ${ formatUSCurrency(hints.debt_mortgage) }.` }

              min={ 0 }
              max={ 2000000 }
              step={ 1000 }
            />

            <FutureFieldSlider
              label='Home Equity Loans Balance'
              name='debt_home_equity'
              mask={ maskCurrencyFromZero }

              error={ errors.debt_home_equity }
              note={ `Your current Home Equity Loans balance from your linked accounts is ${ formatUSCurrency(hints.debt_home_equity) }.` }

              min={ 0 }
              max={ 2000000 }
              step={ 1000 }
            />

            <FutureFieldSlider
              label='Student Loans Balance'
              name='debt_student'
              mask={ maskCurrencyFromZero }

              error={ errors.debt_student }
              note={ `Your current Student Loans balance from your linked accounts is ${ formatUSCurrency(hints.debt_student) }.` }

              min={ 0 }
              max={ 100000 }
              step={ 100 }
            />
          </AccordionTabFocusinPreset>
        </ThemeProvider>
      </div>

      <div className={ styles.tab }>
        <AccordionTabFocusinPreset label='Asset Growth Rates'>
          <FutureFieldSlider
            isPremium
            label='Cash &amp; Savings Yield'
            name='asset_savings_rate'
            mask={ maskPercentFromZero }
            inputMode='decimal'

            error={ errors.asset_savings_rate }
            note={ filterDefaults(hints, 'asset_savings_rate', `Your Cash & Savings from your linked accounts is earning ${ hints.asset_savings_rate }%.`) }

            min={ 0 }
            max={ 5 }
            step={ .1 }
          />

          <FutureFieldSlider
            isPremium
            label='CD Yield'
            name='asset_cd_rate'
            mask={ maskPercentFromZero }
            inputMode='decimal'

            error={ errors.asset_cd_rate }
            note={ filterDefaults(hints, 'asset_cd_rate', `Your CDs from your linked accounts is earning ${ hints.asset_cd_rate }%.`) }

            min={ 0 }
            max={ 10 }
            step={ .1 }
          />

          <FutureFieldButtons
            isPremium
            label='Avg Investments Returns'
            name='asset_investment_rate'
            mask={ maskPercentNeg30To50 }
            inputMode='decimal'

            error={ errors.asset_investment_rate }
            note={ `Long term stock market returns are ${ hints.asset_investment_rate }% per year historically.` }

            min={ -30 }
            max={ 30 }
            step={ 1 }
          />

          <FutureFieldButtons
            isPremium
            label='Other Assets Yield'
            name='asset_other_rate'
            mask={ maskPercentNeg10To20 }
            inputMode='decimal'

            error={ errors.asset_other_rate }

            min={ -10 }
            max={ 20 }
            step={ 1 }
          />

          <FutureFieldButtons
            isPremium
            label='Homes Appreciation'
            name='asset_home_rate'
            mask={ maskPercentNeg10To20 }
            inputMode='decimal'

            error={ errors.asset_home_rate }
            note={ `Homes in ${ hints.state } have historically increased in value by ${ hints.asset_home_rate }% per year.` }

            min={ -10 }
            max={ 10 }
            step={ 1 }
          />

          <FutureFieldButtons
            isPremium
            label='Cars Depreciation'
            name='asset_auto_rate'
            mask={ maskPercentFromZero }
            inputMode='decimal'

            error={ errors.asset_auto_rate }
            note={ `Cars lose ${ hints.asset_auto_rate }% of their value every year on average.` }

            min={ 0 }
            max={ 30 }
            step={ 1 }
          />
        </AccordionTabFocusinPreset>
      </div>

      <div className={ styles.tab }>
        <AccordionTabFocusinPreset label='Other Rates'>
          <FutureFieldButtons
            isPremium
            label='Income Growth Rate'
            name='salary_growth_rate'
            mask={ maskPercentFromZero }
            inputMode='decimal'

            error={ errors.salary_growth_rate }
            note={ `The average person's income grows ${ hints.salary_growth_rate }% each year.` }

            min={ 0 }
            max={ 20 }
            step={ 1 }
          />

          <FutureFieldButtons
            isPremium
            label='Credit Cards Interest Rate'
            name='debt_creditcard_rate'
            mask={ maskPercentFromZero }
            inputMode='decimal'

            error={ errors.debt_creditcard_rate }
            note={ filterDefaults(hints, 'debt_creditcard_rate', `You're paying an average interest rate of ${ hints.debt_creditcard_rate }% on your credit cards.`) }

            min={ 0 }
            max={ 30 }
            step={ 1 }
          />

          <FutureFieldButtons
            isPremium
            label='Tax Rate'
            name='salary_tax_rate'
            mask={ maskPercentFromZero }
            inputMode='decimal'

            error={ errors.salary_tax_rate }
            note={ `People with similar income pay an average of ${ taxHints.salary_tax_rate || hints.salary_tax_rate }% in taxes each year.` }

            min={ 0 }
            max={ 50 }
            step={ 1 }
          />

          <FutureFieldButtons
            isPremium
            label='Inflation Rate'
            name='inflation_rate'
            mask={ maskPercentFromZero }
            inputMode='decimal'

            error={ errors.inflation_rate }
            note='The cost of living has historically increased by about 2% per year.'

            min={ 0 }
            max={ 10 }
            step={ 1 }
          />
        </AccordionTabFocusinPreset>
      </div>
    </div>
  )
}
